"use client";

import { CinematicModal } from "@/components/ui/CinematicModal";
import { trackUpgradeIntent } from "@/lib/analytics/posthog-helpers";
import {
  cinematicModalPrimaryButtonClass,
  cinematicModalSubtitleClass,
  cinematicModalTitleClass,
} from "@/lib/cinematicModalStyles";
import {
  type FreemiumUsageState,
  HEAVY_FREEMIUM_DISCOUNT_COUPON,
  getFreemiumDiscountCoupon,
} from "@/lib/freemium/usage-state";
import { buildTrackedPaywallUrl } from "@/lib/paywallUrl";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";

interface FreemiumPreSessionOfferModalProps {
  isOpen: boolean;
  onContinue: () => void;
  onClose: () => void;
  userEmail?: string | null;
  usageState?: FreemiumUsageState | null;
  variant?: "default" | "zen";
}

export default function FreemiumPreSessionOfferModal({
  isOpen,
  onContinue,
  onClose,
  userEmail,
  usageState,
  variant = "default",
}: FreemiumPreSessionOfferModalProps) {
  const router = useRouter();
  const posthog = usePostHog();
  const isZen = variant === "zen";

  const discountCoupon =
    getFreemiumDiscountCoupon(usageState?.cohort) || HEAVY_FREEMIUM_DISCOUNT_COUPON;

  const commonTracking = {
    email: userEmail || undefined,
    offer_surface: "pre_session_modal",
    offer_mode: "discount",
    coupon_code: discountCoupon,
    freemium_cohort: usageState?.cohort,
    qualified_conversation_count: usageState?.qualifiedConversationCount,
    qualified_conversations_this_week: usageState?.qualifiedConversationsThisWeek,
  };

  const handleUpgrade = () => {
    trackUpgradeIntent(posthog, "pre_session_modal", commonTracking);
    router.push(
      buildTrackedPaywallUrl({
        coupon: discountCoupon,
        mode: isZen ? "zen" : undefined,
        source: "pre_session_modal",
      })
    );
  };

  const handleContinue = () => {
    posthog?.capture("freemium_pre_session_offer_dismissed", {
      ...commonTracking,
      dismiss_action: "continue_free",
    });
    onContinue();
  };

  return (
    <CinematicModal
      isOpen={isOpen}
      onClose={onClose}
      titleId="pre-session-offer-dialog-title"
      panelClassName={isZen ? "border-stone-700/70 bg-stone-950" : undefined}
    >
      <div className="pr-8">
        <h2
          id="pre-session-offer-dialog-title"
          className={cn(cinematicModalTitleClass, isZen && "font-serif text-stone-100")}
        >
          Keep speaking without the timer
        </h2>
        <p className={cn(cinematicModalSubtitleClass, isZen && "text-stone-400")}>
          Unlimited practice
        </p>
      </div>

      <p className={cn("mt-4 text-sm leading-relaxed text-white/75", isZen && "text-stone-300")}>
        You&apos;re already warmed up. Continue free today, or remove the weekly limits and keep the
        conversation moving.
      </p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={handleContinue}
          className={cn(
            "inline-flex h-11 items-center justify-center px-2 text-sm font-semibold text-white/55 transition-colors hover:text-white",
            isZen && "text-stone-400 hover:text-stone-100"
          )}
        >
          Continue free today
        </button>
        <button type="button" onClick={handleUpgrade} className={cinematicModalPrimaryButtonClass}>
          Unlock unlimited practice
        </button>
      </div>
    </CinematicModal>
  );
}
